import { useCallback, useMemo, useSyncExternalStore } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BaseLogo } from "../core/models/entities/BaseLogo";
import { actAsCacheKey } from "../contexts/actAsStore";
import { useApiClient } from "../data/clients/ApiClient";
import { showInfoToast } from "../presentation/components/Toast/Toast";
import {
  ManualRefreshLock,
  ManualRefreshState,
  manualRefreshKey,
  manualRefreshState,
  manualWindowParams,
  NO_MANUAL_REFRESH_LOCK,
  patchStorageInQueryCache,
  requestedStore,
  rowInsideManualWindow,
  storageStampMs,
  STORAGE_COOLDOWN_SKEW_MS,
  STORAGE_FRESHNESS_REQUEST_TTL_MS,
  STORAGE_FRESHNESS_TIMEOUT_MS,
  STORAGE_MANUAL_BACKOFF_MS,
} from "../utils/functions/storageFreshness";
import {
  getApiDisabledUntilMs,
  landFreshnessResponse,
  subscribeApiDisabled,
} from "./useStorageFreshness";
import { useMinuteTick } from "./useMinuteTick";

// Manual "refresh stock now" (flag storageManualRefreshEnabled; Nichlas 8/9:
// the customer must be able to refresh ONE item from NAV right away,
// whatever the stamp's age). Module state, shared by every button on the
// page: the (org, logo) pair with a click in the air - at most one at a
// time, a click on any other row meanwhile costs nothing - and the pause
// every button sits in: the API ignored `force` (its manual flag off) or
// answered enabled=false, or a click could not verify its row. The
// add-to-cart look-up (useStorageRefreshOnAddToCart) is a forced call too
// and shares both: one forced call in the air per page, whoever asked, and
// one pause for "force is not available right now".

const inFlight = new Set<string>();
let lock: ManualRefreshLock = NO_MANUAL_REFRESH_LOCK;
let version = 0;
const listeners = new Set<() => void>();

function notify(): void {
  version += 1;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const unsubscribeRequested = requestedStore.subscribe(listener);
  const unsubscribeApiDisabled = subscribeApiDisabled(listener);
  return () => {
    listeners.delete(listener);
    unsubscribeRequested();
    unsubscribeApiDisabled();
  };
}

// The snapshot must change whenever the state of ANY button may have: our
// own version (in-flight, pause), the requested store's (a mark by the
// on-view call locks the button of that logo too) and the API-disabled
// instant (an on-view answer saying enabled=false pauses every button).
function getSnapshot(): string {
  return `${version}:${requestedStore.version()}:${getApiDisabledUntilMs()}`;
}

function pause(kind: ManualRefreshLock["kind"], untilMs: number): void {
  if (untilMs <= lock.untilMs) return;
  lock = { untilMs, kind };
}

/**
 * The pause in effect: the later of the manual pause and the API-disabled
 * instant learnt from any answer (enabled=false = "unavailable").
 */
export function getManualRefreshLock(): ManualRefreshLock {
  const apiDisabledUntilMs = getApiDisabledUntilMs();
  return apiDisabledUntilMs > lock.untilMs
    ? { untilMs: apiDisabledUntilMs, kind: "unavailable" }
    : lock;
}

/** True while a forced call - a click or an add-to-cart look-up - is in the air. */
export function isManualRefreshInFlight(): boolean {
  return inFlight.size > 0;
}

/**
 * Registers a forced call for these logos of `orgId`: their buttons read
 * "loading", every other button "busy", until endManualRefresh.
 */
export function beginManualRefresh(
  orgId: number,
  logoIds: readonly number[]
): void {
  logoIds.forEach((id) => inFlight.add(manualRefreshKey(orgId, id)));
  notify();
}

export function endManualRefresh(
  orgId: number,
  logoIds: readonly number[]
): void {
  logoIds.forEach((id) => inFlight.delete(manualRefreshKey(orgId, id)));
  notify();
}

/** Pauses every button (see ManualRefreshLock) and tells them at once. */
export function pauseManualRefresh(
  kind: ManualRefreshLock["kind"],
  untilMs: number
): void {
  pause(kind, untilMs);
  notify();
}

export function resetManualStorageRefreshForTests(): void {
  inFlight.clear();
  lock = NO_MANUAL_REFRESH_LOCK;
  version = 0;
  listeners.clear();
}

/**
 * The requested-store mark of a (org, logo) moved back to the stamp the API
 * answered with, so the guard closes when the row's window closes - the
 * same instant the tooltip counts down to - instead of 5 minutes after the
 * call (a click or an add-to-cart look-up). Never moved forward past now (a
 * stamp in the future, clock skew, would prune the other marks early; the
 * stamp's own window covers that), and never so far back that the guard
 * closes in less than the skew margin: a stamp that reads at the window's
 * edge on this clock is still inside it on the API's, and a call a moment
 * later would only meet the window again.
 */
export function alignGuardWithStamp(
  orgId: number,
  logoId: number,
  iso: string | null | undefined
): void {
  const ms = storageStampMs(iso);
  if (ms == null) return;
  const now = Date.now();
  const floor = now - STORAGE_FRESHNESS_REQUEST_TTL_MS + STORAGE_COOLDOWN_SKEW_MS;
  requestedStore.mark(orgId, [logoId], Math.max(Math.min(ms, now), floor));
}

/**
 * The state of the "refresh stock now" button of one logo and the click
 * handler behind it. NOTHING here runs on render or on an effect: only
 * `refresh` - a click - costs a call, and that call is ONE
 * POST organizations/{orgId}/logos/storage-freshness with {logoIds:[id],
 * force:true}, marked in the shared requested store BEFORE it leaves (the
 * in-flight / double-click guard, shared with the on-view call). One manual
 * call per page at a time: every other button is "busy" while it is in the
 * air. The answer is landed exactly like the on-view answer
 * (landFreshnessResponse: API-disabled pause, max age, cache patch in the
 * act-as scope of the click). A row the API verified - forced or, when the
 * row was stale anyway, by its ordinary rule - is the happy path: the count
 * and "just now" land in the cache, nothing is said, and the button is
 * "cooldown" for the API's 5-minute window counted from the NEW stamp -
 * the row's stamp is what locks the button, so the lock holds in every tab
 * and across a reload, exactly as long as the API would refuse a forced
 * call (isInsideManualWindow). Otherwise: no forced=true (API manual flag
 * off) or enabled=false pauses every button for 5 minutes with the "not
 * available" toast; a forced answer given from the API's window (the row
 * not verified in this call but stamped inside the window - another tab's
 * click, the on-view call, a colleague; rowInsideManualWindow) is a
 * cooldown, not a failure: the row's count and stamp land in the cache,
 * one short info toast says when it was updated and when the button opens
 * again, nothing is paused; a row that could not be verified (budget spent,
 * NAV degraded, failed marker - its stamp hours old) or transport trouble
 * pauses every button for STORAGE_MANUAL_BACKOFF_MS with the "could not be
 * refreshed" toast. The state is recomputed on every store change, on every
 * new stamp and once a minute (useMinuteTick), so a cooldown lifts at most
 * 60 s after its window without a timer per row. Call budget: 1 HTTP call
 * per accepted click, 0 otherwise.
 *
 * @param orgId  The organization whose storage row the button refreshes (see
 *               resolveStorageFreshnessOrgId); <= 0 = every click is ignored.
 * @param logo   The logo next to the button: its id and its storage stamp
 *               (storageUpdatedAtUtc) matter.
 */
export function useManualStorageRefresh(
  orgId: number,
  logo: BaseLogo
): { state: ManualRefreshState; refresh: () => void } {
  const queryClient = useQueryClient();
  const { refreshStorageFreshness } = useApiClient();
  const { t } = useTranslation();
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const tick = useMinuteTick();
  const logoId = logo.id;
  const stamp = logo.storageUpdatedAtUtc;

  const state = useMemo(
    () =>
      manualRefreshState(
        orgId,
        logoId,
        Date.now(),
        requestedStore,
        inFlight,
        getManualRefreshLock(),
        stamp
      ),
    // snapshot/tick are the reasons to recompute; they carry no data.
    [orgId, logoId, stamp, snapshot, tick]
  );

  const refresh = useCallback(() => {
    if (!(orgId > 0) || !(logoId > 0)) return;
    const now = Date.now();
    if (
      manualRefreshState(
        orgId,
        logoId,
        now,
        requestedStore,
        inFlight,
        getManualRefreshLock(),
        stamp
      ) !== "idle"
    )
      return;
    requestedStore.mark(orgId, [logoId], now);
    beginManualRefresh(orgId, [logoId]);

    const scope = actAsCacheKey();
    const controller = new AbortController();
    const abortTimer = setTimeout(
      () => controller.abort(),
      STORAGE_FRESHNESS_TIMEOUT_MS
    );
    const unavailable = () => {
      pause("unavailable", Date.now() + STORAGE_FRESHNESS_REQUEST_TTL_MS);
      showInfoToast(t("compLogoDesign.refreshStockUnavailable"), 4000);
    };
    const failed = () => {
      pause("failed", Date.now() + STORAGE_MANUAL_BACKOFF_MS);
      showInfoToast(t("compLogoDesign.refreshStockFailed"), 4000);
    };
    refreshStorageFreshness(orgId, [logoId], controller.signal, { force: true })
      .finally(() => clearTimeout(abortTimer))
      .then((response) => {
        const { enabled } = landFreshnessResponse(response, queryClient, scope);
        const rows = response.rows ?? [];
        if (enabled) {
          const row = rows.find((r) => r.logoId === logoId);
          if (row?.verified) {
            alignGuardWithStamp(
              orgId,
              logoId,
              row.storageUpdatedAtUtc ?? row.verifiedAtUtc
            );
            return;
          }
        }
        if (!enabled || response.forced !== true) {
          unavailable();
          return;
        }
        const answeredAt = Date.now();
        const recent = rowInsideManualWindow(rows, logoId, answeredAt);
        if (recent) {
          // The figure the API holds for a row inside its window IS the one
          // it verified a moment ago: landed like a verified row, so this
          // tab's count and stamp catch up with whoever refreshed it.
          patchStorageInQueryCache(
            queryClient,
            [{ ...recent, verified: true }],
            scope
          );
          alignGuardWithStamp(orgId, logoId, recent.storageUpdatedAtUtc);
          showInfoToast(
            t(
              "compLogoDesign.refreshStockJustUpdated",
              manualWindowParams(recent.storageUpdatedAtUtc, answeredAt, t)
            ),
            4000
          );
          return;
        }
        failed();
      })
      .catch(() => failed())
      .finally(() => endManualRefresh(orgId, [logoId]));
  }, [orgId, logoId, stamp, queryClient, refreshStorageFreshness, t]);

  return { state, refresh };
}
